import { useEffect, useMemo, useState } from "react";
import { api, INR, INR2, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GraduationCap, Home as HomeIcon, HeartPulse, Sparkles, Landmark, Coins, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PURPOSES = [
  { v: "MARRIAGE", label: "Marriage", Icon: Sparkles, tint: "bg-brand-primary/10 text-brand-primary" },
  { v: "EDUCATION", label: "Education", Icon: GraduationCap, tint: "bg-brand-secondary/10 text-brand-secondary" },
  { v: "MEDICAL", label: "Medical", Icon: HeartPulse, tint: "bg-red-100 text-red-800" },
  { v: "BUY_ASSET", label: "Buy Asset", Icon: HomeIcon, tint: "bg-amber-100 text-amber-800" },
  { v: "PLEDGE", label: "Pledge", Icon: Landmark, tint: "bg-brand-accent/20 text-amber-900" },
  { v: "SELL", label: "Sell", Icon: Coins, tint: "bg-brand-slate2 text-brand-cream" },
];

export default function Redeem() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [purpose, setPurpose] = useState("");
  const [grams, setGrams] = useState(0.1);
  const [maxGrams, setMaxGrams] = useState(1);
  const [price, setPrice] = useState(0);
  const [jewellers, setJewellers] = useState([]);
  const [partner, setPartner] = useState("");
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(null);
  const [kyc, setKyc] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, p, j, me] = await Promise.all([
        api.get("/wallet/summary"),
        api.get("/market/price"),
        api.get("/jewellers"),
        api.get("/auth/me"),
      ]);
      setMaxGrams(Math.max(0.1, Number(s.data.grams || 0.1)));
      setGrams(Math.min(0.5, Math.max(0.1, Number(s.data.grams || 0.1))));
      setPrice(p.data.price_24k_inr_per_gram);
      setJewellers(j.data.jewellers || []);
      setKyc(me.data.kyc_status);
    })();
  }, []);

  const inrValue = useMemo(() => Number(grams || 0) * (price || 0), [grams, price]);

  const submit = async () => {
    try {
      const { data } = await api.post("/redeem", {
        purpose,
        grams: Number(grams),
        partner_id: partner || null,
        notes,
      });
      setDone(data.redemption);
      setStep(6);
      toast.success("Redemption initiated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="pt-6 pb-10">
      <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Redeem</div>
      <h1 className="mt-1 font-display text-4xl font-semibold text-brand-charcoal">Turn gold into your goal</h1>

      {/* Progress bar */}
      <div className="mt-5 flex gap-1.5" data-testid="redeem-progress">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand-primary" : "bg-brand-line"}`} />
        ))}
      </div>
      <div className="mt-2 text-xs text-brand-slate">Step {Math.min(step, 5)} of 5</div>

      <div className="mt-5">
        {step === 1 && (
          <div data-testid="redeem-step-1">
            <h2 className="font-display text-2xl text-brand-charcoal">What are you redeeming for?</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {PURPOSES.map((p) => (
                <button
                  key={p.v}
                  data-testid={`purpose-${p.v}`}
                  onClick={() => { setPurpose(p.v); setStep(2); }}
                  className={`rounded-2xl border p-4 text-left transition-transform active:scale-[0.97] ${
                    purpose === p.v ? "border-brand-primary bg-brand-sand" : "border-brand-line bg-brand-ivory"
                  }`}
                >
                  <div className={`h-11 w-11 rounded-full flex items-center justify-center ${p.tint}`}>
                    <p.Icon size={20} />
                  </div>
                  <div className="mt-3 font-semibold text-brand-charcoal">{p.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="redeem-step-2">
            <h2 className="font-display text-2xl text-brand-charcoal">How much gold?</h2>
            <div className="mt-6 rounded-3xl border border-brand-line bg-brand-sand p-5 shadow-soft">
              <div className="font-display text-5xl text-brand-charcoal tabular-nums text-center">
                {Number(grams).toFixed(3)}<span className="text-brand-accent"> g</span>
              </div>
              <div className="text-center mt-1 text-brand-slate tabular-nums">≈ {INR(inrValue)}</div>
              <div className="mt-6">
                <Slider
                  data-testid="redeem-slider"
                  value={[Number(grams)]}
                  onValueChange={(v) => setGrams(Number(v[0].toFixed(3)))}
                  min={0.1}
                  max={maxGrams}
                  step={0.01}
                />
                <div className="mt-2 flex justify-between text-xs text-brand-slate tabular-nums">
                  <span>0.100g</span>
                  <span>Max {maxGrams.toFixed(3)}g</span>
                </div>
              </div>
            </div>
            <Nav onBack={() => setStep(1)} onNext={() => setStep(3)} nextDisabled={grams <= 0} />
          </div>
        )}

        {step === 3 && (
          <div data-testid="redeem-step-3">
            <h2 className="font-display text-2xl text-brand-cream">Choose your partner</h2>
            <p className="text-sm text-brand-slate mt-1">{jewellers.length} registered B2B partners near you.</p>
            <div className="mt-4 space-y-2.5">
              {jewellers.map((j) => {
                const isPlat = j.tier === "Platinum";
                return (
                  <button
                    key={j.id}
                    data-testid={`partner-${j.id}`}
                    onClick={() => setPartner(j.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-transform active:scale-[0.99] relative overflow-hidden ${
                      partner === j.id
                        ? "border-brand-primary bg-brand-sand2 shadow-gold"
                        : "border-brand-line-faint bg-brand-sand hover:border-brand-line"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-11 w-11 shrink-0 rounded-full font-display text-lg font-bold flex items-center justify-center ${isPlat ? "bg-brand-accent text-brand-page" : "bg-brand-primary text-brand-page"}`}>
                        {j.name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-brand-cream truncate">{j.name}</div>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isPlat ? "bg-brand-accent/25 text-brand-accent" : j.tier === "Gold" ? "bg-brand-primary/20 text-brand-primary" : "bg-brand-slate2 text-brand-slate"}`}>
                            {j.tier}
                          </span>
                        </div>
                        <div className="text-xs text-brand-slate mt-0.5">
                          {j.branch} · {j.city} · {j.distance_km}km
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                          <span className="text-brand-accent font-semibold tabular-nums">★ {j.rating}</span>
                          <span className="text-brand-slate2">({j.reviews?.toLocaleString("en-IN")})</span>
                          {j.making_pct > 0 && (
                            <span className="text-brand-slate">· Making {j.making_pct}%</span>
                          )}
                          {j.delivery && <span className="rounded-full bg-brand-secondary/15 text-brand-secondary px-2 py-0.5 font-semibold">Delivery</span>}
                          {j.pickup && <span className="rounded-full bg-brand-primary/15 text-brand-primary px-2 py-0.5 font-semibold">Pickup</span>}
                        </div>
                        {j.offer && (
                          <div className="mt-2 text-[11px] text-brand-accent bg-brand-accent/10 border border-brand-line-faint rounded-lg px-2.5 py-1.5">
                            ✦ {j.offer}
                          </div>
                        )}
                      </div>
                      {partner === j.id && (
                        <div className="h-6 w-6 shrink-0 rounded-full bg-brand-primary text-brand-page flex items-center justify-center text-xs font-bold">
                          ✓
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <Nav onBack={() => setStep(2)} onNext={() => setStep(4)} nextDisabled={!partner} />
          </div>
        )}

        {step === 4 && (
          <div data-testid="redeem-step-4">
            <h2 className="font-display text-2xl text-brand-cream">KYC check</h2>
            <div className="mt-4 rounded-2xl border border-brand-line-faint bg-brand-sand p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <div className={`h-11 w-11 rounded-full flex items-center justify-center ${kyc === "VERIFIED" ? "bg-brand-secondary text-brand-page" : "bg-brand-accent text-brand-page"}`}>
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <div className="font-semibold text-brand-cream">DigiLocker · {kyc || "PENDING"}</div>
                  <div className="text-xs text-brand-slate">{kyc === "VERIFIED" ? "You're all set to proceed." : "One-tap Aadhaar verification via DigiLocker."}</div>
                </div>
              </div>
              {kyc !== "VERIFIED" && (
                <Button
                  data-testid="redeem-kyc-verify"
                  onClick={async () => {
                    try {
                      const { data: init } = await api.post("/kyc/initiate", { redirect_uri: window.location.href });
                      if (init.mock) {
                        const { data: v } = await api.post("/kyc/mock-verify", { reference_id: init.reference_id });
                        setKyc(v.kyc_status);
                        toast.success("KYC verified via DigiLocker");
                      } else {
                        window.location.href = init.auth_url;
                      }
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="mt-4 w-full h-11 rounded-full bg-gold-gradient text-brand-page font-semibold"
                >
                  Verify with DigiLocker
                </Button>
              )}
            </div>
            <Textarea
              data-testid="redeem-notes"
              placeholder="Any notes for the partner (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-4 rounded-xl border-brand-line-faint bg-brand-sand text-brand-cream placeholder:text-brand-slate2"
            />
            <Nav onBack={() => setStep(3)} onNext={() => setStep(5)} nextDisabled={kyc !== "VERIFIED"} />
          </div>
        )}

        {step === 5 && (
          <div data-testid="redeem-step-5">
            <h2 className="font-display text-2xl text-brand-charcoal">Review & confirm</h2>
            <div className="mt-4 rounded-2xl border border-brand-line bg-brand-sand p-5 shadow-soft space-y-2 text-sm">
              <Row k="Purpose" v={PURPOSES.find((p) => p.v === purpose)?.label} />
              <Row k="Grams" v={`${Number(grams).toFixed(4)} g`} />
              <Row k="Est. value" v={INR2(inrValue)} />
              <Row k="Partner" v={jewellers.find((j) => j.id === partner)?.name || "—"} />
              <Row k="KYC" v={kyc} />
            </div>
            <div className="mt-4 flex gap-3">
              <Button variant="outline" data-testid="redeem-back-5" onClick={() => setStep(4)} className="flex-1 h-12 rounded-full border-brand-line">
                <ArrowLeft size={16} /> Back
              </Button>
              <Button data-testid="redeem-confirm" onClick={submit} className="flex-1 h-12 rounded-full bg-brand-primary text-brand-ivory">
                Confirm Redemption
              </Button>
            </div>
          </div>
        )}

        {step === 6 && done && (
          <div data-testid="redeem-done" className="mt-4 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-brand-secondary text-brand-ivory flex items-center justify-center">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="mt-4 font-display text-3xl text-brand-charcoal">Redemption initiated</h2>
            <p className="mt-2 text-brand-slate text-sm">Reference: {done.id.slice(0, 8).toUpperCase()}</p>
            <Button onClick={() => nav("/vault")} className="mt-6 h-12 px-8 rounded-full bg-brand-primary text-brand-ivory">
              Go to Vault
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Nav({ onBack, onNext, nextDisabled }) {
  return (
    <div className="mt-6 flex gap-3">
      <Button variant="outline" onClick={onBack} className="flex-1 h-12 rounded-full border-brand-line">
        <ArrowLeft size={16} /> Back
      </Button>
      <Button
        onClick={onNext}
        disabled={nextDisabled}
        data-testid="redeem-next"
        className="flex-1 h-12 rounded-full bg-brand-primary hover:bg-brand-primary-soft text-brand-ivory"
      >
        Continue <ArrowRight size={16} />
      </Button>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-brand-slate">{k}</div>
      <div className="font-semibold text-brand-charcoal tabular-nums">{v}</div>
    </div>
  );
}
